import React from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Edit, X, Clock, MapPin } from "lucide-react";
import { useSubscription } from '@/context/SubscriptionContext';
import { useToast } from '@/components/ui/use-toast';
import { useTheme } from '@/context/ThemeContext';
import { EditDogForm } from "@/components/EditDogForm";

export const DogItemActions = ({ dog, selectedDayKey, onEdit, onEditClick, onScheduleClick, daysList, currentDate, getInitialDayData, isUrgent, onUrgentClick, isReadOnly }) => {
   const { permissions } = useSubscription();
   const { toast } = useToast();
   const { theme } = useTheme();

   const handleWhatsAppClick = (e) => {
     e.stopPropagation();
     if (isReadOnly) return;
     if (!permissions.canUseQuickWhatsApp) {
       toast({
         title: "Función Premium",
         description: "El acceso rápido a WhatsApp está disponible en planes Premium.",
         variant: "destructive",
       });
       return;
     }
     if (dog.whatsappGroup) {
       window.open(dog.whatsappGroup, "_blank");
     }
   };

  const handleLocationClick = (e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    if (dog.locationUrl) {
      window.open(dog.locationUrl, "_blank");
    }
  };

  return (
    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
      {dog.locationUrl && (
        <Button
          variant="ghost"
          size="icon"
          className={`dog-action text-[hsl(var(--gmaps))] hover:text-[hsl(var(--gmaps))]/90 dark:text-[#e3d02b] dark:hover:text-[#d4c01f] h-[30px] w-[30px] ${theme === 'dark' ? 'bg-transparent hover:bg-transparent p-1.5' : 'bg-slate-700/70 hover:bg-slate-700/85 p-1.5'}`}
          onClick={handleLocationClick}
          disabled={isReadOnly}
          aria-label="Abrir ubicación en Google Maps"
        >
          <MapPin className="h-5 w-5" />
        </Button>
      )}
       {dog.whatsappGroup && (
         <Button
           variant="ghost"
           size="icon"
           className={`dog-action h-9 w-9 p-2 ${permissions.canUseQuickWhatsApp && !isReadOnly ? 'text-green-600 hover:text-green-700 hover:bg-green-100' : 'text-green-400 opacity-50 cursor-default'}`}
           onClick={handleWhatsAppClick}
           disabled={isReadOnly}
           aria-label="Abrir chat de WhatsApp"
         >
           <MessageCircle className="h-5 w-5" />
         </Button>
       )}
      
      <EditDogForm
        dog={dog}
        selectedDayKey={selectedDayKey}
        onEdit={onEdit}
        isOpen={false}
        onOpenChange={(open) => { if (open && !isReadOnly) onEditClick(); }}
        daysList={daysList}
        currentDate={currentDate}
        getInitialDayData={getInitialDayData}
        triggerButton={
          <Button
            variant="ghost"
            size="icon"
            className="dog-action text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-9 w-9 p-2"
            onClick={(e) => { e.stopPropagation(); if (!isReadOnly) onEditClick(); }}
            disabled={isReadOnly}
            aria-label="Editar perro"
          >
            <Edit className="h-5 w-5" />
          </Button>
        }
      />

      <Button
        variant="ghost"
        size="icon"
        className={`dog-action h-9 w-9 p-2 ${isUrgent ? "text-white bg-red-500 hover:bg-red-600" : "text-red-600 hover:text-red-700 hover:bg-red-100"}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!isReadOnly) onUrgentClick(dog.name, dog.id);
        }}
        disabled={isReadOnly}
        aria-label={isUrgent ? "Quitar urgencia" : "Marcar como urgente"}
      >
        <X className="h-5 w-5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="dog-action text-muted-foreground hover:text-foreground dark:hover:text-white/70 h-9 w-9 p-2"
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) onScheduleClick(); }}
        disabled={isReadOnly}
        aria-label="Ver horarios"
      >
        <Clock className="h-5 w-5" />
      </Button>
    </div>
  );
};
